import axios from 'axios';

// In production, API calls go to same origin (nginx proxies to backend)
// In development, use REACT_APP_API_URL or localhost
const API_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_URL,
  timeout: 20000,
  headers: {
    'Content-Type': 'application/json',
  },
});

const INFRA_ERROR_PATTERN = /(AtlasError|Mongo|pymongo|database|collection|not allowed to do action|space quota|jobs_global|job_hound|traceback|stack trace|ECONN|ETIMEDOUT|ENOTFOUND)/i;
const GENERIC_SERVICE_ERROR = 'This service is temporarily unavailable. Please try again in a moment.';

export const getApiErrorMessage = (err, fallback = GENERIC_SERVICE_ERROR) => {
  const detail = err?.response?.data?.detail;
  const status = err?.response?.status;

  if (typeof detail === 'string') {
    if (status >= 500 || INFRA_ERROR_PATTERN.test(detail)) {
      return fallback;
    }
    return detail;
  }

  if (detail?.message && typeof detail.message === 'string') {
    return detail.message;
  }

  if (status >= 500) {
    return fallback;
  }

  return fallback;
};

// Store for Clerk getToken function (set by ClerkProvider)
let getClerkToken = null;

export const setClerkTokenGetter = (getter) => {
  getClerkToken = getter;
};

// Interceptor to add Clerk JWT to requests
api.interceptors.request.use(async (config) => {
  if (getClerkToken) {
    try {
      const token = await getClerkToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch (e) {
      // User not signed in, continue without token
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const detail = error?.response?.data?.detail;
    const status = error?.response?.status;
    if (typeof detail === 'string' && (status >= 500 || INFRA_ERROR_PATTERN.test(detail))) {
      if (!error.response.data) {
        error.response.data = {};
      }
      error.response.data.detail = GENERIC_SERVICE_ERROR;
    }
    return Promise.reject(error);
  }
);

export const getJobs = async () => {
  const response = await api.get('/api/jobs/');
  return response.data;
};

export const createJob = async (jobData) => {
  const response = await api.post('/api/jobs/', jobData);
  return response.data;
};

export const updateJob = async (jobId, updates) => {
  const response = await api.patch(`/api/jobs/${jobId}`, updates);
  return response.data;
};

export const deleteJob = async (jobId) => {
  const response = await api.delete(`/api/jobs/${jobId}`);
  return response.data;
};

// Scraper API
// user = null → public browse/search
// user → authenticated search with match scoring
export const searchJobs = async (params, user) => {
  const endpoint = user ? '/api/scraper/search_user' : '/api/scraper/search_non_user';
  const response = await api.get(endpoint, { params });
  return response.data;
};

export const importJob = async (job) => {
  const response = await api.post('/api/scraper/import', job);
  return response.data;
};

export const saveSearchPreferences = async (prefs) => {
  const response = await api.patch('/api/profile/preferences', prefs);
  return response.data;
};

export const submitJobFeedback = async (sourceId, title, company, location, feedback) => {
  const response = await api.post('/api/scraper/feedback', {
    source_id: sourceId,
    title,
    company,
    location,
    feedback,
  });
  return response.data;
};

// Profile API
export const getInit = async () => {
  const response = await api.get('/api/profile/init');
  return response.data;
};

export const getProfile = async () => {
  const response = await api.get('/api/profile/');
  return response.data;
};

export const saveProfile = async (profile) => {
  const response = await api.post('/api/profile/', profile);
  return response.data;
};

export const uploadResume = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/api/profile/upload-resume', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 120000,
  });
  return response.data;
};

export const regenerateTitles = async () => {
  const response = await api.post('/api/profile/generate-titles');
  return response.data;
};

export const dismissTitle = async (title) => {
  const response = await api.post('/api/profile/dismiss-title', null, { params: { title } });
  return response.data;
};

// Billing API
export const createCheckoutSession = async (successUrl, cancelUrl) => {
  const response = await api.post('/api/billing/create-checkout-session', {
    success_url: successUrl,
    cancel_url: cancelUrl,
  });
  return response.data;
};

export const getSubscriptionStatus = async () => {
  const response = await api.get('/api/billing/subscription-status');
  return response.data;
};

export const getLinkedInRecruiterStatus = async () => {
  const response = await api.get('/api/outreach/linkedin-recruiter-status');
  return response.data;
};

export const claimLinkedInRecruiterAccess = async ({ company, title, location, category, source }) => {
  const response = await api.post('/api/outreach/linkedin-recruiter-access', {
    company,
    title,
    location,
    category,
    source,
  });
  return response.data;
};

export const verifyCheckoutSession = async (sessionId) => {
  const response = await api.post('/api/billing/verify-checkout-session', {
    session_id: sessionId,
  });
  return response.data;
};

export const createPortalSession = async () => {
  const response = await api.post('/api/billing/create-portal-session');
  return response.data;
};

// Feedback API
export const submitFeedback = async (message, type = 'general', page = null) => {
  const response = await api.post('/api/feedback/', {
    message,
    type,
    page: page || window.location.pathname,
    user_agent: navigator.userAgent,
  });
  return response.data;
};

// ============================================================
// Marketplace V2 APIs
// ============================================================

// Role API
export const getRole = async () => {
  const response = await api.get('/api/profile/role');
  return response.data;
};

export const setRole = async (role) => {
  const response = await api.post('/api/profile/role', null, { params: { role } });
  return response.data;
};

// Company API
export const aiGenerateCompanyProfile = async (description) => {
  const response = await api.post('/api/profile/company/ai-generate', { description });
  return response.data;
};

export const updateCompanyInfo = async (data) => {
  const response = await api.patch('/api/profile/company', data);
  return response.data;
};

// Notifications API
export const getNotifications = async (page = 1) => {
  const response = await api.get('/api/notifications/', { params: { page } });
  return response.data;
};

export const getUnreadCount = async () => {
  const response = await api.get('/api/notifications/unread-count');
  return response.data;
};

export const markNotificationRead = async (uuid) => {
  const response = await api.patch(`/api/notifications/${uuid}/read`);
  return response.data;
};

export const markAllNotificationsRead = async () => {
  const response = await api.post('/api/notifications/mark-all-read');
  return response.data;
};

// Provider Jobs API
export const aiGenerateJobPosting = async (description) => {
  const response = await api.post('/api/provider-jobs/ai-generate', { description });
  return response.data;
};

export const createJobPosting = async (data) => {
  const response = await api.post('/api/provider-jobs/', data);
  return response.data;
};

export const getMyJobPostings = async () => {
  const response = await api.get('/api/provider-jobs/');
  return response.data;
};

export const updateJobPosting = async (uuid, data) => {
  const response = await api.patch(`/api/provider-jobs/${uuid}`, data);
  return response.data;
};

export const deleteJobPosting = async (uuid) => {
  const response = await api.delete(`/api/provider-jobs/${uuid}`);
  return response.data;
};

export const getJobInterests = async (uuid) => {
  const response = await api.get(`/api/provider-jobs/${uuid}/interests`);
  return response.data;
};

export const browseProviderJobs = async (params = {}) => {
  const response = await api.get('/api/provider-jobs/browse', { params });
  return response.data;
};

// Interest API (Seeker → Provider Jobs)
export const expressInterest = async (jobUuid, introMessage = null) => {
  const response = await api.post(`/api/jobs/interest/${jobUuid}`, {
    intro_message: introMessage,
  });
  return response.data;
};

export const withdrawInterest = async (jobUuid) => {
  const response = await api.delete(`/api/jobs/interest/${jobUuid}`);
  return response.data;
};

// Cover Letter API
export const generateCoverLetter = async (jobUuid, tone = 'professional') => {
  const response = await api.post('/api/jobs/cover-letter', null, {
    params: { job_uuid: jobUuid, tone },
  });
  return response.data;
};

// Chat API
export const getConversations = async () => {
  const response = await api.get('/api/chat/conversations');
  return response.data;
};

export const createConversation = async (participantUuid, jobUuid = null) => {
  const response = await api.post('/api/chat/conversations', {
    participant_uuid: participantUuid,
    job_uuid: jobUuid,
  });
  return response.data;
};

export const getMessages = async (convUuid, page = 1) => {
  const response = await api.get(`/api/chat/conversations/${convUuid}/messages`, {
    params: { page },
  });
  return response.data;
};

export const sendMessage = async (convUuid, content) => {
  const response = await api.post(`/api/chat/conversations/${convUuid}/messages`, {
    content,
  });
  return response.data;
};

export const markConversationRead = async (convUuid) => {
  const response = await api.post(`/api/chat/conversations/${convUuid}/read`);
  return response.data;
};

export const getChatUnreadCount = async () => {
  const response = await api.get('/api/chat/unread-count');
  return response.data;
};

// Matcher API
export const getCandidates = async (params = {}) => {
  const response = await api.get('/api/matcher/candidates', { params });
  return response.data;
};

export const getMatcherJobs = async (params = {}) => {
  const response = await api.get('/api/matcher/jobs', { params });
  return response.data;
};

export const getCandidateSearchStrategy = async (params = {}) => {
  const response = await api.get('/api/matcher/candidate-search-strategy', { params });
  return response.data;
};

export const generateSourcingPlan = async (data) => {
  const response = await api.post('/api/matcher/sourcing-plan', data);
  return response.data;
};

export const getMatcherRoles = async () => {
  const response = await api.get('/api/matcher/roles');
  return response.data;
};

export const createMatcherRole = async (data) => {
  const response = await api.post('/api/matcher/roles', data);
  return response.data;
};

export const importMatcherRoles = async (data) => {
  const response = await api.post('/api/matcher/roles/import', data);
  return response.data;
};

export const updateMatcherRole = async (uuid, data) => {
  const response = await api.patch(`/api/matcher/roles/${uuid}`, data);
  return response.data;
};

export const getRoleCandidates = async (roleUuid) => {
  const response = await api.get(`/api/matcher/roles/${roleUuid}/candidates`);
  return response.data;
};

export const smartMatchRoleCandidates = async (roleUuid, data) => {
  const response = await api.post(`/api/matcher/roles/${roleUuid}/smart-match`, data);
  return response.data;
};

export const getMyCandidates = async () => {
  const response = await api.get('/api/matcher/my-candidates');
  return response.data;
};

export const createMyCandidate = async (data) => {
  const response = await api.post('/api/matcher/my-candidates', data);
  return response.data;
};

export const importMyCandidates = async (data) => {
  const response = await api.post('/api/matcher/my-candidates/import', data);
  return response.data;
};

export const addManualCandidate = async (data) => {
  const response = await api.post('/api/matcher/candidates/manual', data);
  return response.data;
};

export const updateManualCandidate = async (uuid, data) => {
  const response = await api.patch(`/api/matcher/candidates/manual/${uuid}`, data);
  return response.data;
};

export const createMatch = async (data) => {
  const response = await api.post('/api/matcher/matches', data);
  return response.data;
};

export const getMatches = async (status = null) => {
  const response = await api.get('/api/matcher/matches', {
    params: status ? { status } : {},
  });
  return response.data;
};

export const updateMatch = async (uuid, data) => {
  const response = await api.patch(`/api/matcher/matches/${uuid}`, data);
  return response.data;
};

export const getMatcherStats = async () => {
  const response = await api.get('/api/matcher/stats');
  return response.data;
};

// Feed API
export const getFeed = async (page = 1) => {
  const response = await api.get('/api/feed/', { params: { page } });
  return response.data;
};

export const createPost = async (data) => {
  const response = await api.post('/api/feed/posts', data);
  return response.data;
};

export const likePost = async (uuid) => {
  const response = await api.post(`/api/feed/posts/${uuid}/like`);
  return response.data;
};

export const getComments = async (uuid, page = 1) => {
  const response = await api.get(`/api/feed/posts/${uuid}/comments`, {
    params: { page },
  });
  return response.data;
};

export const addComment = async (uuid, content) => {
  const response = await api.post(`/api/feed/posts/${uuid}/comments`, {
    content,
  });
  return response.data;
};

export const toggleFollow = async (userUuid) => {
  const response = await api.post(`/api/feed/follow/${userUuid}`);
  return response.data;
};

export const discoverUsers = async (page = 1) => {
  const response = await api.get('/api/feed/discover', { params: { page } });
  return response.data;
};

// Export the api instance for use in other services
export default api;